import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { io } from "socket.io-client";
import { useAuth } from "./AuthContext";
import { useOrgFilter } from "./OrgFilterContext";
import { API_BASE_URL } from "@/lib/apiBase";
import { getAccessToken } from "@/lib/authSession";

const PartnerSocketContext = createContext(null);

function pathNeedsPartnerSocket(pathname) {
  const p = String(pathname || "");
  return (
    /\/o\/[^/]+\/messages(\/|$)/.test(p) ||
    /\/messages\/partner/.test(p)
  );
}

/**
 * Socket.IO for cross-org partner channels. Connects lazily when Messages
 * (org) routes need partner realtime — not on every workspace enter.
 */
export function PartnerSocketProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const { selectedOrgId } = useOrgFilter();
  const location = useLocation();
  const socketRef = useRef(null);
  const listenersRef = useRef(new Set());
  const [connected, setConnected] = useState(false);
  const [forceConnect, setForceConnect] = useState(false);

  const orgId =
    selectedOrgId && selectedOrgId !== "social" ? String(selectedOrgId) : null;

  const shouldConnect =
    Boolean(isAuthenticated && orgId) &&
    (forceConnect || pathNeedsPartnerSocket(location.pathname));

  const subscribe = useCallback((handler) => {
    listenersRef.current.add(handler);
    return () => listenersRef.current.delete(handler);
  }, []);

  const emitLocal = useCallback((event, payload) => {
    for (const handler of listenersRef.current) {
      try {
        handler(event, payload);
      } catch (err) {
        console.error("Partner socket listener error:", err);
      }
    }
  }, []);

  const ensureConnected = useCallback(() => {
    setForceConnect(true);
  }, []);

  useEffect(() => {
    if (!orgId) setForceConnect(false);
  }, [orgId]);

  useEffect(() => {
    if (!shouldConnect) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setConnected(false);
      return undefined;
    }

    const socket = io(`${API_BASE_URL}/partner`, {
      auth: (cb) => cb({ token: getAccessToken() }),
      transports: ["websocket", "polling"],
      withCredentials: true,
    });
    socketRef.current = socket;

    const onConnect = () => {
      setConnected(true);
      socket.emit("partner.join", { org_id: orgId });
    };
    const onDisconnect = () => setConnected(false);
    const onMessage = (payload) => emitLocal("partner.message", payload);
    const onUpdated = (payload) =>
      emitLocal("partner.message.updated", payload);
    const onDeleted = (payload) =>
      emitLocal("partner.message.deleted", payload);
    const onReaction = (payload) =>
      emitLocal("partner.message.reaction", payload);
    const onChannel = (payload) => emitLocal("partner.channel.updated", payload);
    const onRead = (payload) => emitLocal("partner.read", payload);
    const onTyping = (payload) => emitLocal("partner.typing", payload);

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("partner.message", onMessage);
    socket.on("partner.message.updated", onUpdated);
    socket.on("partner.message.deleted", onDeleted);
    socket.on("partner.message.reaction", onReaction);
    socket.on("partner.channel.updated", onChannel);
    socket.on("partner.read", onRead);
    socket.on("partner.typing", onTyping);

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("partner.message", onMessage);
      socket.off("partner.message.updated", onUpdated);
      socket.off("partner.message.deleted", onDeleted);
      socket.off("partner.message.reaction", onReaction);
      socket.off("partner.channel.updated", onChannel);
      socket.off("partner.read", onRead);
      socket.off("partner.typing", onTyping);
      socket.disconnect();
      if (socketRef.current === socket) socketRef.current = null;
      setConnected(false);
    };
  }, [shouldConnect, orgId, emitLocal]);

  const joinChannel = useCallback(
    (channelId) => {
      const socket = socketRef.current;
      if (!socket || !orgId || channelId == null) return;
      socket.emit("partner.join", {
        org_id: orgId,
        channel_id: Number(channelId),
      });
    },
    [orgId]
  );

  const emitTyping = useCallback(
    (channelId, typing) => {
      const socket = socketRef.current;
      if (!socket || !orgId || channelId == null) return;
      socket.emit("partner.typing", {
        org_id: orgId,
        channel_id: Number(channelId),
        typing: !!typing,
      });
    },
    [orgId]
  );

  const value = {
    connected,
    orgId,
    subscribe,
    joinChannel,
    emitTyping,
    ensureConnected,
  };

  return (
    <PartnerSocketContext.Provider value={value}>
      {children}
    </PartnerSocketContext.Provider>
  );
}

export function usePartnerSocket() {
  const ctx = useContext(PartnerSocketContext);
  if (!ctx) {
    throw new Error(
      "usePartnerSocket must be used within PartnerSocketProvider"
    );
  }
  return ctx;
}

export function useOptionalPartnerSocket() {
  return useContext(PartnerSocketContext);
}
